import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ListChecks, ChevronDown, ChevronRight, Search, ExternalLink,
  Heart, Shield, FileText, FolderOpen, Mail, Building2, Users,
  Filter, X, ArrowLeft, ShieldAlert, UserCog,
} from 'lucide-react';
import { toast } from 'sonner';
import SeverityPill, { severityRowClass } from '../../components/admin/SeverityPill';

const CATEGORY_ICONS = {
  emotional_safety: Heart,
  account: Shield,
  documents: FolderOpen,
  records: FileText,
  sars: Mail,
  organisations: Building2,
  shared_access: Users,
  admin: UserCog,
  security: ShieldAlert,
};

/** Who did it, when it wasn't the account holder themselves. */
const actorLine = (row) => {
  const a = row.actor || {};
  if (a.type === 'invitee') return `Done by ${row.actor_email || 'someone they shared with'} (shared access)`;
  if (a.type === 'admin') return 'Admin action';
  if (a.type === 'external') return 'Server monitoring';
  if (a.type === 'anonymous') return 'Not signed in';
  if (a.type === 'system') return 'System';
  return null;
};

const PAGE_SIZE = 50;


// =========================================================================
// All-users table view (default landing)
// =========================================================================
const AdminActivityLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [actionGroups, setActionGroups] = useState([]);
  const [data, setData] = useState({ results: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filter state — synced with the URL so links are shareable.
  const [emailQuery, setEmailQuery] = useState(searchParams.get('email') || '');
  const [emailInput, setEmailInput] = useState(searchParams.get('email') || '');
  const [selectedActions, setSelectedActions] = useState(
    (searchParams.get('actions') || '').split(',').filter(Boolean),
  );
  const [emotionalSafetyOnly, setEmotionalSafetyOnly] = useState(
    searchParams.get('emotional_safety_only') === '1',
  );
  const [securityOnly, setSecurityOnly] = useState(searchParams.get('security_only') === '1');
  const [minSeverity, setMinSeverity] = useState(searchParams.get('min_severity') || '');
  const [outcome, setOutcome] = useState(searchParams.get('outcome') || '');
  const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');
  const [page, setPage] = useState(0);

  // Load action catalogue once
  useEffect(() => {
    adminApi.activityActionTypes().then((r) => setActionGroups(r.data?.groups || []));
  }, []);

  const persistFilters = () => {
    const next = new URLSearchParams();
    if (emailQuery) next.set('email', emailQuery);
    if (selectedActions.length) next.set('actions', selectedActions.join(','));
    if (emotionalSafetyOnly) next.set('emotional_safety_only', '1');
    if (securityOnly) next.set('security_only', '1');
    if (minSeverity) next.set('min_severity', minSeverity);
    if (outcome) next.set('outcome', outcome);
    if (startDate) next.set('start_date', startDate);
    if (endDate) next.set('end_date', endDate);
    setSearchParams(next, { replace: true });
  };

  // Auto-persist whenever filters change
  useEffect(() => {
    persistFilters();
    // eslint-disable-next-line
  }, [emailQuery, selectedActions, emotionalSafetyOnly, securityOnly, minSeverity, outcome, startDate, endDate]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (emailQuery) params.email = emailQuery;
    if (emotionalSafetyOnly) {
      params.emotional_safety_only = true;
    } else if (selectedActions.length) {
      params.actions = selectedActions.join(',');
    }
    if (securityOnly) params.security_only = true;
    if (minSeverity) params.min_severity = minSeverity;
    if (outcome) params.outcome = outcome;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    adminApi
      .listActivityLogs(params)
      .then((r) => setData(r.data || { results: [], total: 0 }))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load activity'))
      .finally(() => setLoading(false));
  }, [emailQuery, selectedActions, emotionalSafetyOnly, securityOnly, minSeverity, outcome, startDate, endDate, page]);

  // Reload whenever any filter or page changes.
  useEffect(() => { load(); }, [load]);

  const submitEmail = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setEmailQuery(emailInput.trim());
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const activeFilterCount =
    (emailQuery ? 1 : 0) +
    (emotionalSafetyOnly ? 1 : 0) +
    (selectedActions.length ? 1 : 0) +
    (securityOnly ? 1 : 0) +
    (minSeverity ? 1 : 0) +
    (outcome ? 1 : 0) +
    (startDate || endDate ? 1 : 0);

  return (
    <div className="space-y-6" data-testid="admin-activity-logs-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground">Activity logs</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Permanent, tamper-evident record of every user action, everything done through
            shared access, every admin request and every security signal. Use the
            emotional-safety toggle to focus on what users were shown before an incident, or
            the security toggle for anything that might mean someone got where they shouldn't.
            Warnings and critical events are highlighted; critical ones are also on the{' '}
            <Link to="/admin/security" className="text-brand underline">Security</Link> page.
          </p>
        </div>
      </div>

      {/* Search + filter bar */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <form onSubmit={submitEmail} className="flex items-center gap-2 flex-1 min-w-[260px]">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" />
              <Input
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Search by email (exact match)"
                data-testid="activity-search-input"
                className="max-w-md"
              />
              <Button type="submit" variant="secondary" size="sm" data-testid="activity-search-btn">
                Search
              </Button>
              {emailQuery && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setEmailInput(''); setEmailQuery(''); }}
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
            </form>

            <div className="flex items-center gap-2">
              <Switch
                id="es-only"
                checked={emotionalSafetyOnly}
                onCheckedChange={(v) => { setEmotionalSafetyOnly(v); setPage(0); }}
                data-testid="activity-emotional-toggle"
              />
              <Label htmlFor="es-only" className="text-xs cursor-pointer">
                Emotional safety only
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="sec-only"
                checked={securityOnly}
                onCheckedChange={(v) => { setSecurityOnly(v); setPage(0); }}
                data-testid="activity-security-toggle"
              />
              <Label htmlFor="sec-only" className="text-xs cursor-pointer">
                Security events only
              </Label>
            </div>

            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowFilters((v) => !v)}
              data-testid="activity-toggle-filters"
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2 text-[10px]">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-rule">
              <div className="space-y-1.5">
                <Label className="text-xs">Outcome</Label>
                <Select value={outcome || 'any'} onValueChange={(v) => { setOutcome(v === 'any' ? '' : v); setPage(0); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="failure">Failure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Severity</Label>
                <Select value={minSeverity || 'any'} onValueChange={(v) => { setMinSeverity(v === 'any' ? '' : v); setPage(0); }}>
                  <SelectTrigger data-testid="activity-severity-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="notice">Notice and above</SelectItem>
                    <SelectItem value="warning">Warning and above</SelectItem>
                    <SelectItem value="critical">Critical only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Start date</Label>
                <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(0); }} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">End date</Label>
                <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(0); }} />
              </div>
              <div className="space-y-1.5 lg:col-span-4">
                <Label className="text-xs">Action types</Label>
                <ActionTypeMultiSelect
                  groups={actionGroups}
                  selected={selectedActions}
                  onChange={(v) => { setSelectedActions(v); setPage(0); }}
                  disabled={emotionalSafetyOnly}
                />
                {emotionalSafetyOnly && (
                  <p className="text-[11px] text-muted-foreground">
                    Action filter is locked while "Emotional safety only" is on.
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-sm text-muted-foreground p-6">Loading…</p>
          ) : data.results.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">
              No activity matches the current filters.
            </p>
          ) : (
            <table className="w-full text-sm" data-testid="activity-table">
              <thead>
                <tr className="border-b border-rule text-xs text-muted-foreground">
                  <th className="text-left p-3 font-medium">Timestamp</th>
                  <th className="text-left p-3 font-medium">Severity</th>
                  <th className="text-left p-3 font-medium">User</th>
                  <th className="text-left p-3 font-medium">Action</th>
                  <th className="text-left p-3 font-medium">Detail</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {data.results.map((row) => (
                  <ActivityRow key={row.id} row={row} onView={(uid) => navigate(`/admin/activity-logs/users/${uid}`)} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)} of {data.total}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Previous
            </Button>
            <span>Page {page + 1} / {totalPages}</span>
            <Button
              variant="secondary" size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// =========================================================================
// Single row with expandable metadata
// =========================================================================
const ActivityRow = ({ row, onView }) => {
  const [open, setOpen] = useState(false);
  const Icon = CATEGORY_ICONS[row.category] || ListChecks;
  return (
    <>
      <tr className={`border-b border-rule hover:bg-ground transition-colors duration-state ${severityRowClass(row.severity)}`}>
        <td className="p-3 align-top whitespace-nowrap text-xs text-muted-foreground">
          {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
        </td>
        <td className="p-3 align-top">
          <SeverityPill severity={row.severity} />
        </td>
        <td className="p-3 align-top">
          <div className="flex flex-col">
            <span className="text-foreground text-xs font-medium truncate max-w-[200px]">
              {row.user_email || '—'}
            </span>
            {row.user_full_name && (
              <span className="text-[10px] text-muted-foreground truncate max-w-[200px]">
                {row.user_full_name}
              </span>
            )}
            {actorLine(row) && (
              <span className="text-[11px] text-ink-55 truncate max-w-[220px]">{actorLine(row)}</span>
            )}
          </div>
        </td>
        <td className="p-3 align-top">
          <div className="flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-foreground">{row.label}</span>
          </div>
        </td>
        <td className="p-3 align-top">
          {row.metadata && Object.keys(row.metadata).length > 0 ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
              data-testid={`activity-row-expand-${row.id}`}
            >
              {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {open ? 'Hide detail' : 'Show detail'}
            </button>
          ) : (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </td>
        <td className="p-3 align-top text-right">
          {row.user_id && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => onView(row.user_id)}
              data-testid={`activity-view-user-${row.id}`}
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              View user
            </Button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-ground">
          <td />
          <td colSpan={5} className="p-3">
            <MetadataDetail metadata={row.metadata} ip={row.ip_address} userAgent={row.user_agent} />
          </td>
        </tr>
      )}
    </>
  );
};


// =========================================================================
// Readable metadata panel — never raw JSON
// =========================================================================
const FIELD_LABELS = {
  success: 'Outcome',
  mfa_used: 'MFA challenge',
  mfa_pending: 'MFA pending',
  reason: 'Reason',
  fields_changed: 'Fields changed',
  organisation_type: 'Organisation type',
  new_status: 'New status',
  file_type: 'File type',
  size_bracket: 'File size',
  document_type: 'Document type',
  records_extracted: 'Records extracted',
  confidence: 'Confidence',
  status: 'Status',
  document_id: 'Document ID',
  record_id: 'Record ID',
  duration: 'Duration',
  organisation: 'Organisation',
  category: 'Support category',
  first_time: 'First-time',
  batch: 'Batch',
  method: 'HTTP method',
  route: 'Route',
  area: 'Area',
  verb: 'Viewed or changed',
  targets: 'Target ids',
  record_ids: 'Records',
  document_ids: 'Documents',
  count: 'Count',
  partial: 'Batch (lazy load)',
  purpose: 'Purpose',
  legacy: 'Imported from old log',
};

const FIELD_VALUE_FORMATTERS = {
  success: (v) => (v ? 'success' : 'failure'),
  mfa_used: (v) => (v ? 'yes' : 'no'),
  mfa_pending: (v) => (v ? 'yes' : 'no'),
  fields_changed: (v) => (Array.isArray(v) ? v.join(', ') : String(v)),
  size_bracket: (v) =>
    ({ under_1mb: 'Under 1 MB', '1_to_10mb': '1–10 MB', over_10mb: 'Over 10 MB' }[v] || v),
  confidence: (v) => ({ low: 'Low', medium: 'Medium', high: 'High', unknown: 'Unknown' }[v] || v),
  duration: (v) =>
    ({ under_1_min: 'Under 1 minute', '1_to_5_min': '1–5 minutes', over_5_min: 'Over 5 minutes' }[v] || v),
};

const MetadataDetail = ({ metadata, ip, userAgent }) => {
  const entries = Object.entries(metadata || {}).filter(([, v]) => v !== null && v !== '');
  return (
    <div className="rounded-lg bg-ground p-3 space-y-1.5">
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">No metadata.</p>
      ) : (
        entries.map(([k, v]) => {
          const label = FIELD_LABELS[k] || k.replace(/_/g, ' ');
          const fmt = FIELD_VALUE_FORMATTERS[k];
          const value = fmt ? fmt(v) : (typeof v === 'object' ? JSON.stringify(v) : String(v));
          return (
            <div key={k} className="flex items-baseline gap-2 text-xs">
              <span className="text-muted-foreground capitalize min-w-[140px]">{label}:</span>
              <span className="font-medium text-foreground">{value}</span>
            </div>
          );
        })
      )}
      {(ip || userAgent) && (
        <div className="pt-2 border-t border-rule mt-2 space-y-1 text-[10px] text-muted-foreground">
          {ip && <div>IP: <span className="font-mono">{ip}</span></div>}
          {userAgent && <div>UA: <span className="font-mono">{userAgent.slice(0, 120)}</span></div>}
        </div>
      )}
    </div>
  );
};


// =========================================================================
// Action type multi-select grouped by category
// =========================================================================
const ActionTypeMultiSelect = ({ groups, selected, onChange, disabled }) => {
  const [open, setOpen] = useState(false);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  const summary =
    selected.length === 0
      ? 'All actions'
      : selected.length <= 2
      ? selected.map((id) => {
          for (const g of groups) for (const a of g.actions) if (a.id === id) return a.label;
          return id;
        }).join(', ')
      : `${selected.length} actions selected`;

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left rounded-md bg-ground px-3 py-2.5 text-sm flex items-center justify-between hover:bg-inset transition-colors duration-state disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="activity-actions-select"
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto rounded-md border border-rule bg-popover shadow-overlay p-2 space-y-3">
          {groups.map((g) => (
            <div key={g.category}>
              <p className="text-[11px] font-semibold uppercase text-muted-foreground px-1 py-1">
                {g.category_label}
              </p>
              {g.actions.map((a) => {
                const checked = selected.includes(a.id);
                return (
                  <label
                    key={a.id}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-ground cursor-pointer text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(a.id)}
                    />
                    {a.label}
                  </label>
                );
              })}
            </div>
          ))}
          <div className="pt-2 border-t border-rule flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Close</Button>
          </div>
        </div>
      )}
    </div>
  );
};


// =========================================================================
// User detail view
// =========================================================================
export const AdminActivityLogsUser = () => {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]); // selected action filter
  const [emotionalSafetyOnly, setEmotionalSafetyOnly] = useState(false);
  const [actionGroups, setActionGroups] = useState([]);

  useEffect(() => {
    adminApi.activityActionTypes().then((r) => setActionGroups(r.data?.groups || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 500 };
    if (emotionalSafetyOnly) params.emotional_safety_only = true;
    else if (actions.length) params.actions = actions.join(',');
    adminApi
      .userActivityDetail(userId, params)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed to load user activity'))
      .finally(() => setLoading(false));
  }, [userId, actions, emotionalSafetyOnly]);

  useEffect(() => { load(); }, [load]);

  const [unlocking, setUnlocking] = useState(false);
  const unlock = async () => {
    setUnlocking(true);
    try {
      await adminApi.unlockUser(userId);
      toast.success('Account unlocked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unlock account');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data) return null;

  const u = data.user || {};

  return (
    <div className="space-y-6" data-testid="admin-activity-user-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/activity-logs">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to all activity
          </Link>
        </Button>
      </div>
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">
          {u.full_name || u.email || u.id}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {u.email}{u.role ? ` · ${u.role}` : ''} · joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
        </p>
      </div>

      {u.account_locked && (
        <Card className="bg-breach-wash text-breach-ink" data-testid="account-locked-banner">
          <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-medium">Account locked</p>
              <p className="text-xs mt-0.5">
                Locked after {u.failed_login_attempts ?? '?'} failed sign-in attempts
                {u.locked_at ? ` on ${new Date(u.locked_at).toLocaleString()}` : ''}. The user cannot sign in
                until an admin unlocks the account.
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={unlock}
              disabled={unlocking}
              data-testid="unlock-account-btn"
            >
              {unlocking ? 'Unlocking…' : 'Unlock account'}
            </Button>
          </CardContent>
        </Card>
      )}

      <SafetySummaryCard summary={data.safety_summary} />

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Switch
                id="user-es-only"
                checked={emotionalSafetyOnly}
                onCheckedChange={setEmotionalSafetyOnly}
              />
              <Label htmlFor="user-es-only" className="text-xs">Emotional safety only</Label>
            </div>
            <div className="flex-1 min-w-[260px]">
              <ActionTypeMultiSelect
                groups={actionGroups}
                selected={actions}
                onChange={setActions}
                disabled={emotionalSafetyOnly}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {data.results.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">No activity matches.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-rule text-xs text-muted-foreground">
                  <th className="text-left p-3 font-medium">Timestamp</th>
                  <th className="text-left p-3 font-medium">Severity</th>
                  <th className="text-left p-3 font-medium">Action</th>
                  <th className="text-left p-3 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((row) => (
                  <UserActivityRow key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const UserActivityRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const Icon = CATEGORY_ICONS[row.category] || ListChecks;
  return (
    <>
      <tr className={`border-b border-rule hover:bg-ground ${severityRowClass(row.severity)}`}>
        <td className="p-3 align-top whitespace-nowrap text-xs text-muted-foreground">
          {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
        </td>
        <td className="p-3 align-top">
          <SeverityPill severity={row.severity} />
        </td>
        <td className="p-3 align-top">
          <div className="flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-foreground">{row.label}</span>
          </div>
          {actorLine(row) && <p className="text-[11px] text-ink-55 mt-0.5">{actorLine(row)}</p>}
        </td>
        <td className="p-3 align-top">
          {row.metadata && Object.keys(row.metadata).length > 0 ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-xs text-primary inline-flex items-center gap-1 hover:underline"
            >
              {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {open ? 'Hide detail' : 'Show detail'}
            </button>
          ) : (
            <span className="text-[11px] text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-ground">
          <td />
          <td colSpan={3} className="p-3">
            <MetadataDetail metadata={row.metadata} ip={row.ip_address} userAgent={row.user_agent} />
          </td>
        </tr>
      )}
    </>
  );
};


// =========================================================================
// Emotional safety summary card (top of user detail view)
// =========================================================================
const SafetySummaryCard = ({ summary }) => {
  if (!summary) return null;
  const stat = (label, value) => (
    <div className="flex flex-col">
      <span className="text-2xl font-heading font-semibold text-ink">{value}</span>
      <span className="text-[11px] text-muted-foreground leading-tight">{label}</span>
    </div>
  );
  const walkthroughOutcome =
    summary.walkthrough_completed > 0
      ? 'Completed'
      : summary.walkthrough_skipped > 0
      ? 'Skipped'
      : summary.walkthrough_shown > 0
      ? 'Started, not finished'
      : 'Never shown';
  return (
    <Card data-testid="safety-summary-card">
      <CardContent className="p-5 space-y-4">
        <h2 className="font-medium text-foreground">Emotional safety summary</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
          {stat('Walkthrough shown', summary.walkthrough_shown)}
          {stat('Walkthrough outcome', walkthroughOutcome)}
          {stat('Sensitive warnings shown', summary.sensitive_warnings_shown)}
          {stat('Warnings expanded', summary.sensitive_warnings_expanded)}
          {stat('Warnings dismissed', summary.sensitive_warnings_dismissed)}
          {stat('Support org clicks', summary.support_org_clicks)}
          {stat('Gentle return prompts shown', summary.gentle_return_shown)}
          {stat('Gentle return, support clicked', summary.gentle_return_support_clicked)}
          {stat('Gentle return dismissed', summary.gentle_return_dismissed)}
        </div>
      </CardContent>
    </Card>
  );
};


export default AdminActivityLogs;
